import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../applicationUi/components/Table";
import { format, addDays, startOfWeek, endOfWeek } from "date-fns";
import { fetchClinicianScheduleReport } from "../../../../store/thunks/reportsThunk";
import * as XLSX from "xlsx";
import { toast } from 'react-toastify';

const ClinicianScheduleReport = () => {
  const dispatch = useDispatch();
  const [startDate, setStartDate] = useState(format(startOfWeek(new Date()), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(endOfWeek(new Date()), "yyyy-MM-dd"));
  const [selectedClinician, setSelectedClinician] = useState("all");

  const {
    data: scheduleData,
    loading,
    error,
  } = useSelector((state) => state.reports.clinicianSchedule || { data: null, loading: false, error: null });

  const clinicians = useSelector((state) => state.clinicians?.data || []);

  const data = scheduleData?.data || {};
  const summary = data.summary || {};
  const schedule = data.schedule || [];
  const dailyBreakdown = data.dailyBreakdown || {};

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(fetchClinicianScheduleReport({ startDate, endDate, clinicianId: selectedClinician }));
  };

  const handleDownload = () => {
    if (schedule.length === 0) {
      toast.warning("No data available to download.");
      return;
    }

    const data = schedule.map((item) => ({
      "Clinician": item.clinicianName || "Unknown",
      "Date": item.date,
      "Day": item.dayOfWeek,
      "Scheduled Hours": item.scheduledHours?.toFixed(2) || "0.00",
      "Booked Hours": item.bookedHours?.toFixed(2) || "0.00",
      "Available Hours": item.availableHours?.toFixed(2) || "0.00",
      "Utilization Rate": `${item.utilizationRate?.toFixed(1) || "0.0"}%`,
      "Appointments": item.appointmentCount || 0,
      "Status": item.status || "Available"
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Clinician Schedule");
    XLSX.writeFile(workbook, `Clinician_Schedule_${startDate}_to_${endDate}.xlsx`);
  };

  const scheduleColumns = [
    { header: "Clinician", accessor: "clinicianName" },
    { header: "Date", accessor: "date" },
    { header: "Day", accessor: "dayOfWeek" },
    { header: "Scheduled Hours", accessor: (row) => row.scheduledHours?.toFixed(2) || "0.00" },
    { header: "Booked Hours", accessor: (row) => row.bookedHours?.toFixed(2) || "0.00" },
    { header: "Available Hours", accessor: (row) => row.availableHours?.toFixed(2) || "0.00" },
    { header: "Utilization", accessor: (row) => `${row.utilizationRate?.toFixed(1) || "0.0"}%` },
    { header: "Appointments", accessor: "appointmentCount" },
    { header: "Status", accessor: "status" },
  ];

  if (error) return <div className="text-red-500 p-4">Error: {error}</div>;

  return (
    <div className="space-y-6">
      {/* Date Range and Clinician Filter Form */}
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-sm shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate Clinician Schedule Report</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <label htmlFor="clinician" className="block text-sm font-medium text-gray-700 mb-1">
              Clinician
            </label>
            <select
              id="clinician"
              value={selectedClinician}
              onChange={(e) => setSelectedClinician(e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="all">All Clinicians</option>
              {clinicians.map((clinician) => (
                <option key={clinician._id} value={clinician._id}>
                  {clinician.firstName} {clinician.lastName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </div>
      </form>

      {/* Show loading state */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading schedule data...</p>
          </div>
        </div>
      )}

      {/* Show data only if we have it and not loading */}
      {!loading && summary && Object.keys(summary).length > 0 && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total Clinicians</h4>
              <p className="text-2xl font-bold text-gray-900">{summary.totalClinicians || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total Scheduled Hours</h4>
              <p className="text-2xl font-bold text-blue-600">{summary.totalScheduledHours || "0.00"}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total Booked Hours</h4>
              <p className="text-2xl font-bold text-green-600">{summary.totalBookedHours || "0.00"}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Average Utilization</h4>
              <p className="text-2xl font-bold text-purple-600">{summary.averageUtilization || "0.0"}%</p>
            </div>
          </div>

          {/* Schedule Table */}
          <div className="bg-white shadow-sm rounded-sm">
            <div className="p-6">
              <Table
                columns={scheduleColumns}
                data={schedule}
                title="Clinician Schedule Breakdown"
                description="Daily schedule breakdown for all clinicians in the selected date range"
                actions={[
                  {
                    label: "Download Excel",
                    onClick: handleDownload,
                  },
                ]}
              />
            </div>
          </div>

          {/* Daily Breakdown by Clinician */}
          {Object.keys(dailyBreakdown).length > 0 && (
            <div className="bg-white shadow-sm rounded-sm">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Daily Breakdown by Clinician</h3>
                <div className="space-y-4">
                  {Object.entries(dailyBreakdown).map(([clinicianName, days]) => (
                    <div key={clinicianName} className="border border-gray-200 rounded-sm p-4">
                      <h4 className="font-medium text-gray-900 mb-3">{clinicianName}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-2">
                        {Object.entries(days).map(([date, dayData]) => (
                          <div key={date} className="bg-gray-50 p-3 rounded-sm text-center">
                            <div className="text-xs font-medium text-gray-600">{format(new Date(date), "MMM dd")}</div>
                            <div className="text-xs text-gray-500">{format(new Date(date), "EEE")}</div>
                            <div className="mt-1">
                              <div className="text-sm font-semibold text-gray-900">
                                {dayData.bookedHours}h / {dayData.scheduledHours}h
                              </div>
                              <div className="text-xs text-gray-600">
                                {dayData.appointmentCount} appts
                              </div>
                              <div className={`text-xs font-medium mt-1 ${
                                dayData.utilizationRate >= 80 ? "text-green-600" :
                                dayData.utilizationRate >= 60 ? "text-yellow-600" : "text-red-600"
                              }`}>
                                {dayData.utilizationRate}%
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Show message if no data and not loading */}
      {!loading && (!summary || Object.keys(summary).length === 0) && (
        <div className="bg-white p-6 rounded-sm shadow-sm text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
          <p className="text-gray-600">
            No schedule data found for the selected date range and clinician(s). Try adjusting your filters or check if there are scheduled appointments in the system for this period.
          </p>
        </div>
      )}
    </div>
  );
};

export default ClinicianScheduleReport;